import { Box, Button, Typography } from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import strings from "global/constants/StringConstants";
import { fillCopy } from "helpers/methods";
import {
  PromoCampaign,
  promoDiscounts,
  promoEndShort,
  promoPercentFor,
} from "helpers/promotionHelpers";
import {
  planPromoDecoPaths,
  planPromoThemes,
  planPromoTokens,
} from "screens/Settings/MyPlan/PlanPromotions.tokens";

/*
  Seasonal promotion pieces for Settings → My Plan. Which campaign (if any) is
  running, and whether the viewer may claim it, is decided by the caller via
  `usePromoCampaign` — these only render.
*/

const t = planPromoTokens;
const copy = strings.promotions.plan;

const campaignBadge = (campaign: PromoCampaign) =>
  fillCopy(strings.promotions.popup[campaign].badge, {
    endShort: promoEndShort(campaign),
  });

const PromoDeco = ({ campaign, sx }: { campaign: PromoCampaign; sx: object }) => (
  <Box
    component="svg"
    aria-hidden="true"
    viewBox="0 0 120 120"
    fill="none"
    stroke="currentColor"
    strokeWidth="7"
    strokeLinejoin="round"
    strokeLinecap="round"
    sx={{
      position: "absolute",
      pointerEvents: "none",
      color: planPromoThemes[campaign].decoColor,
      ...sx,
    }}
  >
    {planPromoDecoPaths[campaign].map((d) => (
      <path key={d} d={d} />
    ))}
  </Box>
);

const badgeSx = (fontSize: string, padding: string) => ({
  alignSelf: "flex-start",
  fontSize,
  fontWeight: 800,
  letterSpacing: "0.1em",
  lineHeight: 1.2,
  color: t.color.onDark,
  backgroundColor: t.color.badgeBg,
  borderRadius: "999px",
  padding,
});

/** Plan summary: "Your Black Friday savings" with both offers and a way in. */
export const PlanPromoBanner = ({
  campaign,
  onViewOffer,
}: {
  campaign: PromoCampaign;
  onViewOffer: () => void;
}) => {
  const theme = planPromoThemes[campaign];
  const discount = promoDiscounts[campaign];
  const offers = [
    { pct: discount.annual, label: copy.offerAnnual },
    { pct: discount.monthly, label: copy.offerMonthly },
  ];

  return (
    <Box
      component="section"
      aria-label={fillCopy(copy.aria, { name: copy[campaign].name })}
      sx={{
        position: "relative",
        overflow: "hidden",
        borderRadius: t.banner.radius,
        background: theme.bg,
        color: t.color.onDark,
        padding: t.banner.padding,
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: t.banner.gap,
        boxShadow: t.color.shadow,
      }}
    >
      <PromoDeco
        campaign={campaign}
        sx={{ right: -14, top: -18, width: t.banner.decoSize, height: t.banner.decoSize }}
      />
      <Box
        sx={{
          position: "relative",
          flex: "1 1 260px",
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: "7px",
        }}
      >
        <Box component="span" sx={badgeSx(t.banner.badgeSize, "4px 10px")}>
          {campaignBadge(campaign)}
        </Box>
        <Typography
          component="h2"
          sx={{
            m: 0,
            fontSize: t.banner.headSize,
            fontWeight: 800,
            lineHeight: 1.15,
            letterSpacing: "-0.02em",
            color: t.color.onDark,
          }}
        >
          {copy[campaign].bannerHead}
        </Typography>
        <Typography sx={{ fontSize: "0.75rem", fontWeight: 500, lineHeight: 1.5, color: t.color.bannerSub }}>
          {copy.bannerSub}
        </Typography>
      </Box>
      <Box sx={{ position: "relative", display: "flex", gap: "8px", flexWrap: "wrap" }}>
        {offers.map((offer) => (
          <Box
            key={offer.label}
            sx={{
              padding: "9px 13px",
              borderRadius: "11px",
              background: t.color.offerBg,
              border: t.color.offerBorder,
              display: "flex",
              flexDirection: "column",
              gap: "2px",
              minWidth: t.banner.offerMinWidth,
              boxSizing: "border-box",
            }}
          >
            <Box
              component="span"
              sx={{
                fontSize: t.banner.offerNumSize,
                fontWeight: 800,
                letterSpacing: "-0.02em",
                lineHeight: 1.1,
                color: theme.accent,
                whiteSpace: "nowrap",
              }}
            >
              {fillCopy(copy.offerOff, { pct: offer.pct })}
            </Box>
            <Box
              component="span"
              sx={{ fontSize: "0.71875rem", fontWeight: 500, color: t.color.offerSub, whiteSpace: "nowrap" }}
            >
              {offer.label}
            </Box>
          </Box>
        ))}
      </Box>
      <Button
        variant="contained"
        onClick={onViewOffer}
        endIcon={<ArrowForwardRoundedIcon />}
        sx={{ position: "relative", whiteSpace: "nowrap" }}
      >
        {strings.promotions.viewOffer}
      </Button>
    </Box>
  );
};

/** "Choose your plan": the headline offer above the seat counter. */
export const PlanPromoStrip = ({ campaign }: { campaign: PromoCampaign }) => {
  const theme = planPromoThemes[campaign];
  const discount = promoDiscounts[campaign];

  return (
    <Box
      component="section"
      aria-label={fillCopy(copy.aria, { name: copy[campaign].name })}
      sx={{
        position: "relative",
        overflow: "hidden",
        borderRadius: t.strip.radius,
        background: theme.bg,
        color: t.color.onDark,
        padding: t.strip.padding,
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: t.strip.gap,
        boxShadow: t.color.shadow,
      }}
    >
      <PromoDeco
        campaign={campaign}
        sx={{
          right: { xs: -30, lg: -18 },
          top: { xs: -34, lg: -26 },
          width: t.strip.decoSize,
          height: t.strip.decoSize,
        }}
      />
      <Box
        sx={{
          position: "relative",
          flex: "1 1 380px",
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: "9px",
        }}
      >
        <Box component="span" sx={badgeSx(t.strip.badgeSize, "5px 12px")}>
          {campaignBadge(campaign)}
        </Box>
        <Typography
          component="h2"
          sx={{
            m: 0,
            fontSize: t.strip.headSize,
            fontWeight: 800,
            lineHeight: 1.1,
            letterSpacing: "-0.025em",
            color: t.color.onDark,
          }}
        >
          {`${copy.saveUpTo} `}
          <Box component="span" sx={{ color: theme.accent }}>
            {`${discount.annual}%`}
          </Box>
        </Typography>
        <Typography sx={{ fontSize: "0.875rem", fontWeight: 500, lineHeight: 1.5, color: t.color.stripLine }}>
          {fillCopy(copy.choiceLine, discount)}
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            fontSize: "0.78125rem",
            fontWeight: 500,
            color: t.color.stripNote,
          }}
        >
          <Box
            component="svg"
            aria-hidden="true"
            viewBox="0 0 24 24"
            fill="none"
            stroke={theme.accent}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            sx={{ width: 17, height: 17, flexShrink: 0 }}
          >
            <circle cx="12" cy="12" r="9" />
            <path d="M8.5 12.2l2.4 2.4 4.6-4.8" />
          </Box>
          {copy.autoApplied}
        </Box>
      </Box>
    </Box>
  );
};

/** Checkout: which campaign discount the order carries. */
export const PlanPromoNote = ({
  campaign,
  isAnnual,
}: {
  campaign: PromoCampaign;
  isAnnual: boolean;
}) => {
  const theme = planPromoThemes[campaign];
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "stretch",
        borderRadius: t.note.radius,
        border: `1px solid ${t.color.noteBorder}`,
        overflow: "hidden",
      }}
    >
      <Box aria-hidden="true" sx={{ flex: `0 0 ${t.note.stubWidth}px`, background: theme.stubBg }} />
      <Box sx={{ flex: 1, padding: "11px 14px", display: "flex", flexDirection: "column", gap: "3px" }}>
        <Box
          component="span"
          sx={{ fontSize: t.note.eyebrowSize, fontWeight: 600, letterSpacing: "0.14em", color: theme.eyebrow }}
        >
          {copy[campaign].shortBadge}
        </Box>
        <Box component="span" sx={{ fontSize: t.note.lineSize, fontWeight: 600, color: t.color.noteText }}>
          {fillCopy(isAnnual ? copy.appliedAnnual : copy.appliedMonthly, {
            pct: promoPercentFor(campaign, isAnnual),
          })}
        </Box>
      </Box>
    </Box>
  );
};
